import { useEffect, useState } from 'react';
import { X, AlertTriangle, Settings2, Palette, LayoutPanelTop } from 'lucide-react';
import { api, ApiError } from '@/services/api';
import type { SiteWidget } from '@/types';
import { getEditor, getWidgetMeta } from './WidgetEditorRegistry';
import { StyleEditor } from './StyleEditor';
import { PlacementEditor } from './PlacementEditor';
import type { WidgetStyles, WidgetPlacement } from './style-types';

interface Props {
  siteId: string;
  siteWidget: SiteWidget;
  onClose: () => void;
  onSaved: () => void;
}

type TabKey = 'content' | 'style' | 'placement';

const TABS: Array<{ key: TabKey; label: string; icon: typeof Settings2 }> = [
  { key: 'content', label: 'Inhalt', icon: Settings2 },
  { key: 'style', label: 'Stil', icon: Palette },
  { key: 'placement', label: 'Platzierung', icon: LayoutPanelTop },
];

export function WidgetConfigModal({ siteId, siteWidget, onClose, onSaved }: Props) {
  const [config, setConfig] = useState<Record<string, unknown>>(siteWidget.config);
  const [styles, setStyles] = useState<WidgetStyles>(
    (siteWidget.styles as WidgetStyles | undefined) ?? {},
  );
  const [placement, setPlacement] = useState<WidgetPlacement>(
    (siteWidget.placement as WidgetPlacement | undefined) ?? {},
  );
  const [activeTab, setActiveTab] = useState<TabKey>('content');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const type = siteWidget.widget?.type ?? '';
  const meta = getWidgetMeta(type);
  const Editor = getEditor(type);
  const Icon = meta.icon;

  const handleConfigChange = (next: Record<string, unknown>) => {
    setConfig(next);
    setDirty(true);
  };
  const handleStylesChange = (next: WidgetStyles) => {
    setStyles(next);
    setDirty(true);
  };
  const handlePlacementChange = (next: WidgetPlacement) => {
    setPlacement(next);
    setDirty(true);
  };

  const handleClose = () => {
    if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/sites/${siteId}/widgets/${siteWidget.id}`, {
        config,
        styles,
        placement,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-8 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="w-full max-w-3xl rounded-2xl border border-surface-300 bg-surface-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-surface-300 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-200">
              <Icon className={`h-5 w-5 ${meta.color}`} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-neutral-100">
                {siteWidget.widget?.name ?? meta.label} konfigurieren
              </h2>
              {siteWidget.widget?.description && (
                <p className="text-xs text-neutral-500">{siteWidget.widget.description}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-neutral-500 hover:bg-surface-200 hover:text-neutral-200"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-surface-300 px-5 pt-3">
          {TABS.map(({ key, label, icon: TabIcon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'border-brand-500 text-neutral-100'
                    : 'border-transparent text-neutral-500 hover:text-neutral-300'
                }`}
              >
                <TabIcon className="h-3.5 w-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {activeTab === 'content' && <Editor config={config} onChange={handleConfigChange} />}
          {activeTab === 'style' && (
            <StyleEditor
              styles={styles}
              onChange={handleStylesChange}
              previewLabel={siteWidget.widget?.name ?? meta.label}
            />
          )}
          {activeTab === 'placement' && (
            <PlacementEditor placement={placement} onChange={handlePlacementChange} />
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 border-t border-surface-300 bg-red-900/10 px-5 py-3 text-xs text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-surface-300 px-5 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg px-4 py-2 text-xs text-neutral-400 hover:text-neutral-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
